import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import { modalActions } from '../../../Modals/store';
import { useDispatch, useSelector } from 'react-redux';
import styles from './SchedulerTask.module.scss';
import useBlockSize from '../../../../utils/useBlockSize';
import clx from 'classnames';
import useWindowSize from '../../../../utils/useWindowSize';
import clsx from 'clsx';
import { badgetList, selectBadges } from '../../constants';
import { Badgets } from '../../../Common/Badgets';
import { ReactComponent as BadIcon } from 'assets/svg/icons/warning_user.svg';
import { allBadClients } from 'Containers/Clients/store/selectors';
import {
  getClientDisplayName,
  getPrimaryServiceName,
  getSplitCardLayout,
  isCalendarBreak,
} from '../utils/taskDisplay';
import { getReadableTextColor } from '../utils/getReadableTextColor';
import { emitSchedulerPointerDrag } from '../utils/pointerDragBus';
import {
  POINTER_DRAG_THRESHOLD_PX,
  armSchedulerClickShield,
  useMobileTaskGestures,
} from '../utils/mobileTaskGestures';
import {
  claimMobileDragHintSlot,
  dismissMobileDragHint,
  isMobileDragHintSeen,
  releaseMobileDragHintSlot,
} from '../utils/mobileDragHint';
import { HIDE_TASK_ICONS_PARAM, HIDE_TASK_TIME_PARAM, isClosureFilterOn } from '../utils/closureHelpers';
import useCoarsePointer from '../utils/useCoarsePointer';
import { useTranslation } from 'react-i18next';
import { Coffee, GripVertical, Lock, X } from 'lucide-react';
import { getMaxVisibleBadges } from './badgeLayout';

const SLOT_MINUTES = 5;
const BREAK_INK = '#8A5420';

const canEditTask = (selectedTask, searchParams) => {
  if (!selectedTask?.isActive) return false;
  if (localStorage.getItem('staff')) {
    return searchParams?.get('allowEdit') == 1;
  }
  return true;
};

const getTaskLayout = (task, day, stepSize) => {
  const fallbackTop = task.top ?? 0;
  const fallbackHeight = task.size ?? 0;

  if (day?.startWork == null || task.unixStartTime == null || task.unixEndTime == null) {
    return { top: fallbackTop, height: fallbackHeight };
  }

  // Clip alla finestra visibile del giorno: le chiusure 00:00→apertura
  // altrimenti hanno top negativo e il label "CHIUSO" resta fuori viewport.
  const visibleStart = Math.max(task.unixStartTime, day.startWork);
  const visibleEnd = Math.min(
    task.unixEndTime,
    day.endWork == null ? task.unixEndTime : day.endWork,
  );

  if (visibleEnd <= visibleStart) {
    return { top: fallbackTop, height: 0 };
  }

  const top = ((visibleStart - day.startWork) / 60 / SLOT_MINUTES) * stepSize;
  const height = ((visibleEnd - visibleStart) / 60 / SLOT_MINUTES) * stepSize;

  return {
    top: Number.isFinite(top) ? top : fallbackTop,
    height: Number.isFinite(height) ? Math.max(height, 0) : fallbackHeight,
  };
};

const BreakCardBody = ({ compact, micro, timeRange, label }) => {
  if (micro) {
    return (
      <div className={styles.break_micro}>
        <Coffee size={12} strokeWidth={2.4} className={styles.break_icon} aria-hidden="true" />
        <span className={styles.break_label_text}>{label}</span>
        <span className={styles.break_time}>{timeRange}</span>
      </div>
    );
  }

  if (compact) {
    return (
      <div className={clsx(styles.break_body, styles.break_body_compact)}>
        <span className={styles.break_badge}>
          <Coffee size={12} strokeWidth={2.4} aria-hidden="true" />
          {label}
        </span>
        <span className={styles.break_time}>{timeRange}</span>
      </div>
    );
  }

  return (
    <div className={styles.break_body}>
      <span className={styles.break_icon_wrap} aria-hidden="true">
        <Coffee size={22} strokeWidth={2.2} />
      </span>
      <span className={styles.break_title}>{label}</span>
      <span className={styles.break_time}>{timeRange}</span>
    </div>
  );
};

const getBreakCardStyle = () => ({
  color: BREAK_INK,
  '--task-ink': BREAK_INK,
});

const SchedulerTask = ({
  selectedTask,
  day,
  setStartData,
  workerId,
  stepSize,
  searchParams,
}) => {
  const { t } = useTranslation();
  const badClients = useSelector(allBadClients());
  const dispatch = useDispatch();
  const observedDiv = useRef(null);
  /** Sessione pointer SOLO desktop (mouse). Mobile = FSM touch esclusiva. */
  const pointerSessionRef = useRef(null);
  const skipClickOpenRef = useRef(false);
  const windowPointerEndRef = useRef(null);
  /** Dopo un drag/scroll, blocca aperture dettaglio residue (iOS/Android). */
  const suppressTaskOpenUntilRef = useRef(0);
  const [dragArmed, setDragArmed] = useState(false);
  const [dragPending, setDragPending] = useState(false);
  const [showDragHint, setShowDragHint] = useState(false);
  const { isMobile } = useWindowSize();
  const isCoarsePointer = useCoarsePointer();
  /** Mobile + tablet: drag solo dalla maniglia, così lo scroll sulla card non sposta. */
  const isTouchDrag = isMobile || isCoarsePointer;
  const editable = canEditTask(selectedTask, searchParams);

  const clearLongPressTimer = (session = pointerSessionRef.current) => {
    if (session?.longPressTimer) {
      clearTimeout(session.longPressTimer);
      session.longPressTimer = null;
    }
  };

  const detachWindowPointerEnd = () => {
    if (!windowPointerEndRef.current) return;
    const { up, cancel } = windowPointerEndRef.current;
    window.removeEventListener('pointerup', up, true);
    window.removeEventListener('pointercancel', cancel, true);
    windowPointerEndRef.current = null;
  };

  const resetPointerSession = ({ clearStartData = false } = {}) => {
    const session = pointerSessionRef.current;
    clearLongPressTimer(session);
    detachWindowPointerEnd();
    pointerSessionRef.current = null;
    setDragArmed(false);
    if (clearStartData) setStartData({});
  };

  useEffect(() => {
    return () => {
      clearLongPressTimer();
      detachWindowPointerEnd();
      pointerSessionRef.current = null;
    };
  }, []);

  const { width: sizeValueWidth, height: sizeValue } = useBlockSize({
    observedBlock: observedDiv,
    sizes: [
      { from: 0, to: 60, value: 1 },
      { from: 60, to: 195, value: 2 },
      { from: 195, to: 300, value: 3 },
      { from: 300, to: 610, value: 4 },
      { from: 610, to: Infinity, value: 5 },
    ],
  });

  const iconSize =
    selectedTask.unixSize < 700 ? Math.min(stepSize, 12) + 5 : Math.min(stepSize, 12) + 10;
  const taskInk = selectedTask.textColor || getReadableTextColor(selectedTask.color);
  const activeBadges = Object.entries(selectBadges(selectedTask) || {}).filter(([, value]) => value);
  const showTaskIcons = !isClosureFilterOn(searchParams, HIDE_TASK_ICONS_PARAM);
  const showTaskTime = !isClosureFilterOn(searchParams, HIDE_TASK_TIME_PARAM);

  const openTaskInfoModal = () => {
    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type: 'taskInfoModal',
        props: {
          day,
          workerId,
          task: selectedTask,
          isBadClient: badClients?.find((client) => client?.utente === selectedTask.utente),
          searchParams,
        },
      }),
    );
  };

  const openOverlapBookingsModal = (event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (!selectedTask.overlapGroupTasks?.length) return;

    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type: 'overlapBookingsModal',
        props: {
          overlapTasks: selectedTask.overlapGroupTasks,
          day,
          workerId,
          searchParams,
        },
      }),
    );
  };

  const handleSplitCardClick = () => {
    if (isMobile && selectedTask.overlapGroupTasks?.length > 1) {
      openOverlapBookingsModal();
      return;
    }
    openTaskInfoModal();
  };

  const openTaskFromInteraction = ({ fromPointer = false } = {}) => {
    if (Date.now() < suppressTaskOpenUntilRef.current) return;
    if (fromPointer) {
      skipClickOpenRef.current = true;
      if (isTouchDrag) armSchedulerClickShield();
    }
    if (selectedTask.isSplitOverlap) handleSplitCardClick();
    else openTaskInfoModal();
  };

  const markDragConsumedOpen = () => {
    suppressTaskOpenUntilRef.current = Date.now() + 700;
    skipClickOpenRef.current = true;
    if (isTouchDrag) armSchedulerClickShield();
  };

  const { handleRef: mobileDragHandleRef } = useMobileTaskGestures({
    enabled: isTouchDrag,
    selectedTask,
    day,
    workerId,
    setStartData,
    setDragArmed,
    setDragPending,
    suppressOpenUntilRef: suppressTaskOpenUntilRef,
  });

  const showMobileDragGrip =
    isTouchDrag && editable && !selectedTask.limitCard && !isCalendarBreak(selectedTask);

  useEffect(() => {
    if (!showMobileDragGrip) return undefined;
    if (!claimMobileDragHintSlot()) return undefined;
    setShowDragHint(true);
    return () => {
      setShowDragHint(false);
      if (!isMobileDragHintSeen()) {
        releaseMobileDragHintSlot();
      }
    };
  }, [showMobileDragGrip]);

  useEffect(() => {
    if (!dragArmed || !showDragHint) return;
    dismissMobileDragHint();
    setShowDragHint(false);
  }, [dragArmed, showDragHint]);

  const closeDragHint = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    dismissMobileDragHint();
    setShowDragHint(false);
  };

  const stopCardClick = (event) => {
    event.stopPropagation();
  };

  const stackOrder = selectedTask.left || 0;
  const isBreak = isCalendarBreak(selectedTask);
  const cardZIndex = (isBreak ? 3 : 2) + stackOrder;
  // Hold/drag: alza shell + card sopra le altre.
  const shellBoost = dragPending || dragArmed ? 40 : 0;
  // Mai sotto empty_slot (z-index 1): altrimenti su Android il tap finisce sullo slot vuoto
  const shellZIndex = selectedTask.hovered ? 2 : cardZIndex + 1 + shellBoost;
  const raisedCardZIndex = cardZIndex + shellBoost;
  const { top: taskTop, height: taskHeight } = getTaskLayout(selectedTask, day, stepSize);
  const isMicroCard = taskHeight <= 48;
  const isCompactCard = !isMicroCard && (sizeValue === 1 || taskHeight < 56);
  const maxVisibleBadges = getMaxVisibleBadges({
    showIcons: showTaskIcons,
    isBreak,
    isMicroCard,
    isCompactCard,
    isMobile,
    taskHeight,
    widthBucket: sizeValueWidth,
  });
  const visibleBadges = activeBadges.slice(0, maxVisibleBadges);
  const badgesCompact =
    isCompactCard || isMobile || taskHeight < 88 || (sizeValueWidth > 0 && sizeValueWidth < 140);
  const badgeIconSizePx = badgesCompact
    ? Math.max(14, Math.min(stepSize, 10) + 6)
    : iconSize;
  const clientName = getClientDisplayName(selectedTask);
  const serviceName = getPrimaryServiceName(selectedTask.servizi);
  const timeRange = `${dayjs(selectedTask.orario_start, 'HH:mm:ss').format('HH:mm')} - ${dayjs(selectedTask.orario_end, 'HH:mm:ss').format('HH:mm')}`;
  const pauseLabel = t('scheduler.schedulerWorker.status.pause');

  const getCardOpacity = () => {
    if (selectedTask.isInteractionSource) return '0.38';
    if (selectedTask.isResizePreview) return '0.92';
    if (selectedTask.dragged) return '0.85';
    if (selectedTask.matched) return 1;
    if (!selectedTask.isActive) return '0.5';
    return selectedTask.opacity || 1;
  };

  const finishPointerSession = (event, { fromCancel = false } = {}) => {
    const session = pointerSessionRef.current;
    if (!session || session.pointerId !== event.pointerId) return;

    clearLongPressTimer(session);
    const wasArmed = session.armed;
    const wasMoved = session.moved;
    const type = session.type;
    const sourceWorkerId = session.sourceWorkerId;
    const target = session.target || event.currentTarget;

    detachWindowPointerEnd();
    pointerSessionRef.current = null;
    setDragArmed(false);

    try {
      target?.releasePointerCapture?.(event.pointerId);
    } catch (_) {
      /* already released */
    }

    if (wasArmed && wasMoved) {
      markDragConsumedOpen();
      emitSchedulerPointerDrag({
        phase: 'end',
        type,
        clientX: event.clientX,
        clientY: event.clientY,
        sourceWorkerId,
      });
      return;
    }

    setStartData({});

    if (type !== 'dragged') return;
    if (fromCancel) {
      skipClickOpenRef.current = true;
      return;
    }
    if (Date.now() < suppressTaskOpenUntilRef.current) return;
    if (!wasMoved) openTaskFromInteraction({ fromPointer: true });
  };

  const endPointerSession = (event) => finishPointerSession(event, { fromCancel: false });
  const cancelPointerSession = (event) => finishPointerSession(event, { fromCancel: true });

  const attachWindowPointerEnd = (pointerId) => {
    detachWindowPointerEnd();
    const up = (event) => {
      if (event.pointerId !== pointerId) return;
      endPointerSession(event);
    };
    const cancel = (event) => {
      if (event.pointerId !== pointerId) return;
      cancelPointerSession(event);
    };
    window.addEventListener('pointerup', up, true);
    window.addEventListener('pointercancel', cancel, true);
    windowPointerEndRef.current = { up, cancel };
  };

  /** Desktop only: mouse/pointer drag + resize. Tablet/touch usa la maniglia. */
  const beginPointerSession = (event, type) => {
    if (isTouchDrag) return false;
    if (!editable) return false;
    if (event.pointerType === 'mouse' && event.button !== 0) return false;

    event.preventDefault();
    event.stopPropagation();
    pointerSessionRef.current = {
      pointerId: event.pointerId,
      type,
      startX: event.clientX,
      startY: event.clientY,
      startTime: Date.now(),
      maxDistance: 0,
      moved: false,
      armed: true,
      startDataSet: true,
      sourceWorkerId: workerId,
      target: event.currentTarget,
      longPressTimer: null,
    };
    setDragArmed(true);
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch (_) {
      /* ignore */
    }
    attachWindowPointerEnd(event.pointerId);
    setStartData({
      type,
      workerId,
      task: selectedTask,
      day,
    });
    return true;
  };

  const handlePointerMove = (event) => {
    if (isTouchDrag) return;
    const session = pointerSessionRef.current;
    if (!session || session.pointerId !== event.pointerId) return;

    const dx = event.clientX - session.startX;
    const dy = event.clientY - session.startY;
    const distance = Math.hypot(dx, dy);
    session.maxDistance = Math.max(session.maxDistance || 0, distance);

    if (!session.armed) return;

    event.preventDefault();
    event.stopPropagation();

    if (!session.moved && distance < POINTER_DRAG_THRESHOLD_PX) return;
    if (!session.moved) {
      session.moved = true;
      markDragConsumedOpen();
    }
    emitSchedulerPointerDrag({
      phase: 'move',
      type: session.type,
      clientX: event.clientX,
      clientY: event.clientY,
      sourceWorkerId: session.sourceWorkerId,
    });
  };

  const renderDragLayer = ({ left, width, zIndex, showBadges = false }) => {
    // Preview in movimento: niente hit-area. Per le passate (!editable) resta il click per aprire/disdire.
    if (selectedTask.dragged) return null;
    return (
      <div
        className={styles.drag_shell}
        style={{
          top: `${taskTop}px`,
          left,
          zIndex,
          width,
          height: `${taskHeight}px`,
          cursor: editable ? 'grab' : 'pointer',
        }}
      >
        <div
          className={clsx(styles.drag_hit, styles.drag_hit_touch, {
            [styles.drag_hit_dragging]: dragArmed,
          })}
          data-drag-role="shell"
          data-dragging={dragArmed ? 'true' : undefined}
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (Date.now() < suppressTaskOpenUntilRef.current) return;
            if (skipClickOpenRef.current) {
              skipClickOpenRef.current = false;
              return;
            }
            if (dragArmed || pointerSessionRef.current?.moved) return;
            if (pointerSessionRef.current) {
              resetPointerSession({ clearStartData: true });
            }
            // Touch: tap nativo (click). Dopo uno scroll il browser non emette click.
            openTaskFromInteraction({ fromPointer: isTouchDrag });
          }}
          onPointerDown={(event) => {
            if (isTouchDrag) return;
            if (editable) beginPointerSession(event, 'dragged');
          }}
          onPointerMove={!isTouchDrag && editable ? handlePointerMove : undefined}
          onPointerUp={!isTouchDrag && editable ? endPointerSession : undefined}
          onPointerCancel={!isTouchDrag && editable ? cancelPointerSession : undefined}
        />
        {showMobileDragGrip ? (
          <div className={styles.mobile_drag_grip_wrap}>
            <button
              ref={mobileDragHandleRef}
              type="button"
              className={clsx(styles.mobile_drag_grip, {
                [styles.mobile_drag_grip_pending]: dragPending,
                [styles.mobile_drag_grip_active]: dragArmed,
              })}
              aria-label={t('scheduler.schedulerTask.dragHandle', {
                defaultValue: 'Sposta prenotazione',
              })}
              data-drag-role="mobile-grip"
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <GripVertical
                size={taskHeight < 40 ? 12 : 14}
                strokeWidth={2.5}
                aria-hidden="true"
              />
            </button>
          </div>
        ) : null}
        {showDragHint && typeof document !== 'undefined'
          ? createPortal(
              <div className={styles.mobile_drag_hint_banner} role="status">
                <span className={styles.mobile_drag_hint_icon} aria-hidden="true">
                  <GripVertical size={16} strokeWidth={2.5} />
                </span>
                <span className={styles.mobile_drag_hint_text}>
                  {t('scheduler.schedulerTask.dragHint')}
                </span>
                <button
                  type="button"
                  className={styles.mobile_drag_hint_close}
                  aria-label={t('scheduler.schedulerTask.dragHintDismiss')}
                  onClick={closeDragHint}
                >
                  <X size={16} strokeWidth={2.5} aria-hidden="true" />
                </button>
              </div>,
              document.body,
            )
          : null}
        {showBadges && visibleBadges.length ? (
          <div
            className={clsx(styles.create_icons_container, {
              [styles.create_icons_container_compact]: badgesCompact,
              [styles.create_icons_container_mobile]: isMobile,
            })}
            draggable={false}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            onDragStart={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            {visibleBadges.map(([key]) => (
              <Badgets
                iconSizeSmall={`${badgeIconSizePx}px`}
                key={key}
                icon={badgetList[key]?.icon}
                text={
                  key == 3
                    ? `${badgetList[key]?.text} ${dayjs(selectedTask.birthday).format('DD MMM')}`
                    : badgetList[key]?.text
                }
                hover={true}
                compact={badgesCompact}
              />
            ))}
          </div>
        ) : null}
        {editable && !isTouchDrag ? (
          <div
            className={styles.drag_handle}
            data-drag-role="resize"
            draggable={false}
            onDragStart={(e) => e.preventDefault()}
            style={{ cursor: 'row-resize' }}
            onPointerDown={(event) => beginPointerSession(event, 'resize')}
            onPointerMove={handlePointerMove}
            onPointerUp={endPointerSession}
            onPointerCancel={cancelPointerSession}
          />
        ) : null}
      </div>
    );
  };

  if (selectedTask.overlapHidden) {
    return null;
  }

  if (selectedTask.isSplitOverlap) {
    const splitLayout = getSplitCardLayout(
      selectedTask.overlapIndex,
      selectedTask.overlapMaxColumns,
    );
    const splitZ = 100 + (selectedTask.overlapIndex || 0);

    return (
      <>
        {renderDragLayer({
          left: splitLayout.left,
          width: splitLayout.width,
          zIndex: splitZ + 1,
        })}
        <div
          className={clsx(styles.card_container, styles.card_container_split, {
            [styles.card_container_mobile]: isMobile,
            [styles.card_break]: isBreak,
            [styles.card_dark_ink]:
              !isBreak &&
              (selectedTask.textColor || getReadableTextColor(selectedTask.color)) === '#1F1F1F',
          })}
          style={{
            top: `${taskTop}px`,
            left: splitLayout.left,
            width: splitLayout.width,
            zIndex: splitZ,
            height: `${taskHeight}px`,
            ...(isBreak
              ? getBreakCardStyle()
              : {
                  background: selectedTask.color,
                  color: selectedTask.textColor || getReadableTextColor(selectedTask.color),
                  '--task-ink': selectedTask.textColor || getReadableTextColor(selectedTask.color),
                }),
            opacity: getCardOpacity(),
            cursor: editable ? 'grab' : 'pointer',
            pointerEvents: 'none',
          }}
        >
          {isBreak ? (
            <BreakCardBody
              compact={taskHeight < 72}
              micro={taskHeight <= 48}
              timeRange={timeRange}
              label={pauseLabel}
            />
          ) : (
            <div className={styles.card_wrapper}>
              <div className={styles.service}>
                <div className={styles.service_box}>
                  <div className={styles.service_box_info}>
                    {(selectedTask.servizi || []).map((service, index) => (
                      <div key={index} className={styles.service_box_info_name}>
                        {service.nome}
                      </div>
                    ))}
                  </div>
                </div>
                <div className={styles.footer}>
                  <div className={styles.footer_client}>{getClientDisplayName(selectedTask)}</div>
                  <div className={styles.footer_box}>
                    {showTaskTime ? (
                      <div className={styles.footer_box_hour}>
                        {dayjs(selectedTask.orario_start, 'HH:mm:ss').format('HH:mm')} -{' '}
                        {dayjs(selectedTask.orario_end, 'HH:mm:ss').format('HH:mm')}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          )}
          {selectedTask.showOverlapBadge && selectedTask.overlapExtraCount > 0 ? (
            <button
              type="button"
              className={styles.split_overflow_badge}
              style={{ pointerEvents: 'auto' }}
              onClick={openOverlapBookingsModal}
              onMouseDown={stopCardClick}
              onPointerDown={stopCardClick}
              aria-label={`Mostra ${selectedTask.overlapExtraCount} altra${
                selectedTask.overlapExtraCount > 1 ? 'e' : ''
              } prenotazione${selectedTask.overlapExtraCount > 1 ? 'i' : ''} sovrapposta${
                selectedTask.overlapExtraCount > 1 ? 'e' : ''
              }`}
            >
              +{selectedTask.overlapExtraCount}
            </button>
          ) : null}
        </div>
      </>
    );
  }

  return (
    <>
      {selectedTask.limitCard ? (
        <div
          className={styles.limit_card}
          style={{
            top: `${taskTop}px`,
            height: `${taskHeight}px`,
          }}
        >
          {taskHeight > 28 ? (
            <span className={styles.limit_card_label}>
              <Lock size={11} strokeWidth={2.4} />
              {t('scheduler.schedulerWorker.status.closed')}
            </span>
          ) : null}
        </div>
      ) : (
        <div>
          {renderDragLayer({
            left: '1%',
            width: '98%',
            zIndex: shellZIndex,
            showBadges: true,
          })}
          {selectedTask.hovered ? (
            <div
              className={clsx(styles.create_card_container, { [styles.card_break]: isBreak })}
              style={{
                top: `${taskTop}px`,
                height: `${taskHeight}px`,
                ...(isBreak ? getBreakCardStyle() : { background: selectedTask.color }),
              }}
            >
              {isBreak ? (
                <BreakCardBody compact timeRange={timeRange} label={pauseLabel} />
              ) : (
                <div className={styles.service_extra_row}>
                  <div className={styles.time_in_service_column}>
                    {dayjs(selectedTask.orario_start, 'HH:mm:ss').format('HH:mm')} -{' '}
                    {dayjs(selectedTask.orario_end, 'HH:mm:ss').format('HH:mm')}
                  </div>
                  <div className={styles.worker_name_in_service_column}>{selectedTask.nome}</div>
                </div>
              )}
            </div>
          ) : (
            <>
              <div
                className={clsx(styles.card_container, {
                  [styles.card_container_mobile]: isMobile,
                  [styles.card_compact]: isCompactCard && !isBreak,
                  [styles.card_micro]: isMicroCard && !isBreak,
                  [styles.card_break]: isBreak,
                  [styles.card_dark_ink]: !isBreak && taskInk === '#1F1F1F',
                  [styles.card_resize_preview]: selectedTask.isResizePreview,
                  [styles.card_interaction_source]: selectedTask.isInteractionSource,
                })}
                ref={observedDiv}
                style={{
                  top: `${taskTop}px`,
                  left: '1%',
                  zIndex: selectedTask.isResizePreview ? 80 : raisedCardZIndex,
                  width: !selectedTask.dragged
                    ? '98%'
                    : `calc(100% - ${selectedTask.left * 10 + 10}px)`,
                  height: `${taskHeight}px`,
                  ...(isBreak
                    ? getBreakCardStyle()
                    : {
                        background: selectedTask.color,
                        color: taskInk,
                        '--task-ink': taskInk,
                      }),
                  opacity: getCardOpacity(),
                  pointerEvents: 'none',
                }}
                aria-label={isBreak ? `${pauseLabel} ${timeRange}` : undefined}
              >
                {selectedTask.isResizePreview ? (
                  <div className={styles.resize_preview_body}>
                    <span className={styles.resize_preview_time}>
                      {dayjs(selectedTask.orario_start, 'HH:mm:ss').format('HH:mm')}
                      {' – '}
                      {dayjs(selectedTask.orario_end, 'HH:mm:ss').format('HH:mm')}
                    </span>
                    {!isMicroCard ? (
                      <span className={styles.resize_preview_name} title={clientName}>
                        {clientName}
                      </span>
                    ) : null}
                  </div>
                ) : isBreak ? (
                  <BreakCardBody
                    compact={isCompactCard}
                    micro={isMicroCard}
                    timeRange={timeRange}
                    label={pauseLabel}
                  />
                ) : isMicroCard ? (
                  <div className={styles.micro_body}>
                    <div className={styles.micro_text}>
                      {serviceName ? (
                        <span className={styles.micro_service} title={serviceName}>
                          {serviceName}
                        </span>
                      ) : null}
                      <span className={styles.micro_client} title={clientName}>
                        {clientName}
                      </span>
                    </div>
                  </div>
                ) : (
                <div
                  className={clx(styles.card_wrapper, {
                    [styles.cardSize0]: stepSize < 14 && sizeValue == 1,
                    [styles.cardSize1]: sizeValue === 1,
                    [styles.cardSize2]: sizeValue === 2,
                    [styles.cardSize3]: sizeValue === 3,
                    [styles.cardSize4]: sizeValue === 4,
                    [styles.cardSize5]: sizeValue === 5,
                  })}
                >
                  <div className={styles.service}>
                    <div className={styles.service_box}>
                      <div className={styles.service_box_info}>
                        {selectedTask.servizi?.map((service, index) => (
                          <div key={index} className={styles.service_box_info_name}>
                            {service.nome}
                          </div>
                        ))}
                      </div>
                      <div className={styles.service_box_icon} />
                    </div>
                    <div className={styles.footer}>
                      <div className={styles.footer_client}>
                        {clientName}
                        {badClients?.find(a=> a?.utente === selectedTask.utente) && 
                          <BadIcon style={{marginRight: 'auto', maxWidth: '14px', height: '14px', marginBottom: '5px'}}/>
                        }
                      </div>
                      <div className={styles.footer_box}>
                        {showTaskTime &&
                        !(isMobile && (sizeValue === 1 || isCompactCard || isMicroCard)) ? (
                          <div className={styles.footer_box_hour}>
                            {timeRange}
                          </div>
                        ) : null}
                        {!isCompactCard ? (
                          <div className={styles.footer_box_icons}>
                            {visibleBadges.map(([key]) => (
                              <div
                                key={key}
                                style={{ width: `${Math.min(stepSize, 20) + 12}px` }}
                              />
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default SchedulerTask;



